// src/client/pages/login.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@client/components/ui/card";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Label } from "@client/components/ui/label";
import { Alert, AlertDescription } from "@client/components/ui/alert";
import { useAuth } from '@client/contexts/auth-context';
import { Loader2, Eye, EyeOff, AlertCircle, CheckCircle, LogIn } from "lucide-react";
import type { LoginPageProps } from '@client/types';

// ============================================================================
// Validation Functions
// ============================================================================

function validateLoginForm(username: string, password: string): string | null {
  if (!username.trim()) {
    return "Please enter username";
  }
  
  if (!password.trim()) {
    return "Please enter password";
  }
  
  if (username.length < 2) {
    return "Username must be at least 2 characters";
  }
  
  if (password.length < 3) {
    return "Password must be at least 3 characters";
  }
  
  return null;
}

// ============================================================================
// Alert Components
// ============================================================================

interface AlertMessageProps {
  message: string;
  variant: 'error' | 'success';
}

function AlertMessage({ message, variant }: AlertMessageProps) {
  if (variant === 'error') {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{message}</AlertDescription>
      </Alert>
    );
  }
  
  return (
    <Alert className="border-green-200 bg-green-50 text-green-800">
      <CheckCircle className="h-4 w-4" />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

// ============================================================================
// Password Input Component
// ============================================================================

interface PasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  disabled: boolean;
  showPassword: boolean;
  onTogglePassword: () => void;
}

function PasswordInput({
  value,
  onChange,
  onKeyPress,
  disabled,
  showPassword,
  onTogglePassword
}: PasswordInputProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor="password">Password</Label>
      <div className="relative">
        <Input
          id="password"
          type={showPassword ? "text" : "password"}
          placeholder="••••••••"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={onKeyPress}
          disabled={disabled}
          className="pr-10"
          autoComplete="current-password"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
          onClick={onTogglePassword}
          disabled={disabled}
          tabIndex={-1}
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4 text-gray-400" />
          ) : (
            <Eye className="h-4 w-4 text-gray-400" />
          )}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Main Login Component
// ============================================================================

export default function Login(_props: LoginPageProps) {
  // Form state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // ============================================================================
  // Effects
  // ============================================================================

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      console.log('[Login] Already authenticated, redirecting to dashboard...');
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Clear messages when user types
  useEffect(() => {
    if (error || success) {
      setError(null);
      setSuccess(null);
    }
  }, [username, password]);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous messages
    setError(null);
    setSuccess(null);

    // Validate form
    const validationError = validateLoginForm(username, password);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    
    try {
      const result = await login(username.trim(), password);
      
      if (result.success) {
        setSuccess(`Welcome back, ${username}!`);
        // Navigation happens via useEffect when isAuthenticated changes
      } else {
        setError(result.message || "Login failed. Please check your credentials.");
      }
    } catch (error) {
      console.error('[Login] Unexpected error:', error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading && username.trim() && password.trim()) {
      handleLogin(e as any);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  // ============================================================================
  // Helper Functions
  // ============================================================================

  const isFormValid = username.trim().length > 0 && password.trim().length > 0;

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center mb-2">
            <div className="p-3 bg-blue-100 rounded-full">
              <LogIn className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">
            TM Application
          </CardTitle>
          <CardDescription className="text-slate-600">
            Sign in to access your dashboard
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Alert Messages */}
          {error && <AlertMessage message={error} variant="error" />}
          {success && <AlertMessage message={success} variant="success" />}

          <form onSubmit={handleLogin} className="space-y-4">
            {/* Username Input */}
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                autoComplete="username"
                autoFocus
              />
            </div>
            
            {/* Password Input */}
            <PasswordInput
              value={password}
              onChange={setPassword}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              showPassword={showPassword}
              onTogglePassword={handleTogglePassword}
            />
            
            {/* Login Button */}
            <Button 
              type="submit"
              disabled={!isFormValid || isLoading}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          {/* Additional Info */}
          <div className="pt-4 text-center">
            <p className="text-xs text-muted-foreground">
              Backend Platform
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}